import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import AppNavbar from "../../components/home/Navbar";
import Footer from "../../components/footer/Footer";
import Seo, { SITE_URL } from "../../components/seo/Seo";
import { addToCart } from "../../redux/actions/CartActions";
import {
  GUIDE_UPDATED,
  findArticle,
  guideArticles,
} from "../../content/guide/articles";
import NotFound from "../NotFound";
import { articlePage } from "../../content/pages";
import "../styles/guide.css";

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="guide-article-h2">{block.text}</h2>;
    case "list": {
      const List = block.ordered ? "ol" : "ul";

      return (
        <List className="guide-article-list">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </List>
      );
    }
    case "table":
      return (
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th key={index}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return block.link ? (
        <p>
          <Link to={`/guide/${block.link.slug}`}>{block.text}</Link>
        </p>
      ) : (
        <p>{block.text}</p>
      );
  }
}

function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.authUser);
  const article = findArticle(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!article) return <NotFound />;

  const url = `${SITE_URL}/guide/${article.slug}`;
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.seoTitle,
      description: article.description,
      dateModified: GUIDE_UPDATED,
      mainEntityOfPage: url,
      image: `${SITE_URL}/og-image.png`,
      author: { "@type": "Organization", name: "Platform98" },
      publisher: {
        "@type": "Organization",
        name: "Platform98",
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/images/logo-Cplatform.png`,
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Guide de l'entrepreneur",
          item: `${SITE_URL}/guide`,
        },
        { "@type": "ListItem", position: 3, name: article.title, item: url },
      ],
    },
  ];

  // Same start as the home page buttons
  const startOrder = async () => {
    const { cta } = article;

    if (cta.path) {
      navigate(cta.path);
      return;
    }

    await dispatch(addToCart({ from: cta.from }));
    navigate(isAuthenticated && user ? "/business-country" : "/account-check");
  };

  const others = guideArticles.filter((item) => item.slug !== article.slug);

  return (
    <>
      <Seo {...articlePage(article)} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <AppNavbar />

      <main className="guide">
        <nav className="guide-breadcrumb" aria-label="Fil d'Ariane">
          <Link to="/">Accueil</Link>
          <span aria-hidden="true">/</span>
          <Link to="/guide">Guide de l'entrepreneur</Link>
        </nav>

        <article className="guide-article">
          <h1 className="guide-article-title">{article.title}</h1>

          {article.blocks.map((block, index) => (
            <Block key={index} block={block} />
          ))}

          <aside className="guide-cta">
            <p>
              Platform98 s'occupe de vos démarches en ligne, sans déplacement.
            </p>
            <button
              type="button"
              className="guide-cta-button"
              onClick={startOrder}
            >
              {article.cta.label} <FiArrowRight aria-hidden="true" />
            </button>
          </aside>
        </article>

        <section className="guide-others" aria-labelledby="guide-others">
          <h2 id="guide-others" className="guide-others-title">
            À lire aussi
          </h2>
          <ul className="guide-list">
            {others.map((item) => (
              <li key={item.slug}>
                <Link to={`/guide/${item.slug}`} className="guide-card">
                  <h3 className="guide-card-title">{item.title}</h3>
                  <p className="guide-card-summary">{item.summary}</p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default GuideArticle;
